import { useEffect, useState } from 'react';

import type { DisplayTimeZone } from '../api/learner';
import { getLearnerClasses } from '../api/learner';
import { getHouseholdCalendar } from '../api/schedule';
import { UTC_ZONE } from './zonedTime';

/** The zone a family screen draws in and the server's clock, as the server last said them. */
export interface FamilyTime {
  tz: DisplayTimeZone;
  serverNow?: string;
}

const INITIAL: FamilyTime = { tz: UTC_ZONE };

/**
 * The household's display zone, for screens that span every learner (the
 * dashboard, My Learning). Read from the household calendar's envelope with an
 * empty range: the server resolves the zone — the household's, else UTC — and
 * nothing here falls back to the browser's. Until it answers, or if
 * it cannot, times are drawn in UTC and labelled so.
 */
export function useHouseholdTime(): FamilyTime {
  const [time, setTime] = useState<FamilyTime>(INITIAL);
  useEffect(() => {
    let live = true;
    // An instant, not a window: only the envelope is wanted.
    const probe = new Date().toISOString();
    Promise.resolve()
      .then(() => getHouseholdCalendar(probe, probe))
      .then((res) => {
        if (live && res?.timeZone) setTime({ tz: res.timeZone, serverNow: res.serverNow });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return time;
}

/**
 * One learner's display zone, for screens about that learner (progress, past
 * classes): the same envelope the learner's own screens read, so the parent
 * and the child see one class at one time.
 */
export function useLearnerTime(studentId: string | null | undefined): FamilyTime {
  const [time, setTime] = useState<FamilyTime>(INITIAL);
  useEffect(() => {
    if (!studentId) return;
    let live = true;
    Promise.resolve()
      .then(() => getLearnerClasses(studentId))
      .then((res) => {
        if (live && res?.timeZone) setTime({ tz: res.timeZone, serverNow: res.serverNow });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [studentId]);
  return time;
}
